// Orquestación del lease de mesa de ESTE dispositivo (Fase 1 TPV).
//
// Singleton (no un hook) para que el lease sobreviva al cambio de layout
// TPV grande ⇄ carta camarero: el bridge de cada layout llama a
// `setTarget(mesaId)`; al desmontar pide `setTarget(null)` con un margen
// (RELEASE_GRACE_MS) que se cancela si el siguiente layout vuelve a pedir
// la misma mesa.
//
// Si el backend aún no expone los endpoints (404/405) o hay error de red,
// el TPV sigue funcionando sin lease (degradación silenciosa): nunca
// bloqueamos la operativa por la presencia.

import axios from "src/utils/axios";
import { store } from "src/redux/store";
import { getDeviceId } from "src/utils/deviceId";
import { ensureDeviceSessionIdentifier } from "src/utils/deviceSession";
import { isDeviceRequiredError } from "src/utils/deviceSessionCore";
import journal from "src/utils/journal/journal";
import {
  HEARTBEAT_MS,
  LEASE_MODE_EDIT,
  RELEASE_GRACE_MS,
  heartbeatLeaseIds,
  leaseErrorInfo,
  lostMesaIds,
  lastPaymentFromEvent,
  decideLostLease,
} from "src/utils/mesaLeaseCore";
import { receiveLastPayment } from "src/redux/slices/session";
import {
  getCurrentLease,
  setCurrentLease,
  clearCurrentLease,
  setLeaseHeldHandler,
} from "src/utils/mesaLeaseRegistry";
import {
  myLeaseSet,
  myLeaseRemoved,
  leaseRevoked,
  setChargingPrompt,
  leasesSnapshotReceived,
  reacquireHandled,
} from "src/redux/slices/mesaLeases";

const dispatch = (a) => store.dispatch(a);
const getState = () => store.getState();

let target = null; // { mesaId, mode }
let releaseTimer = null;
let releasePendingMesa = null; // mesa que se soltará cuando venza releaseTimer
let heartbeatTimer = null;
let inflight = null; // promesa del acquire en curso
let unsupported = false; // backend sin endpoints de lease → no insistir

const myLeases = () => getState()?.mesaLeases?.myLeases || {};

// ── Resultado del lease al entrar en una mesa ──────────────────────
// La carga de la cuenta al pulsar una mesa espera a saber si la mesa es
// nuestra: con 409 LEASE_CHARGING (otro dispositivo cobrando) la cuenta no
// se pinta nunca, solo el aviso «¿Forzar?» (regla 2026-10-07).
const acquiring = new Set(); // mesas con acquire en curso

// ── Solo dentro del TPV (regla usuario 2026-10-07) ─────────────────
// El lease, su heartbeat y la presencia (GET mesas/leases) solo viven en las
// rutas del TPV. Fuera (Configuración, Estadísticas…) no se pide nada: lo
// marca MesaLeaseBridge según la ruta. Por defecto activo (tests y montajes
// sin bridge mantienen el comportamiento anterior).
let tpvRouteActive = true;
export function setTpvRouteActive(active) {
  tpvRouteActive = !!active;
}
export const isTpvRouteActive = () => tpvRouteActive;
const settleWaiters = new Map(); // mesaId → [resolve]

function settleLease(mesaId, result) {
  const key = String(mesaId);
  acquiring.delete(key);
  const waiters = settleWaiters.get(key);
  if (!waiters) return;
  settleWaiters.delete(key);
  waiters.forEach((resolve) => resolve(result));
}

/** Espera al acquire en curso de `mesaId` (lo resuelve `settleLease`). */
function waitAcquire(key) {
  return new Promise((resolve) => {
    const waiters = settleWaiters.get(key) || [];
    waiters.push(resolve);
    settleWaiters.set(key, waiters);
  });
}

/**
 * Resultado del lease de `mesaId` para ESTA entrada en la mesa (pulsar el
 * mosaico, volver a pulsarla, restaurar tras recargar, cancelar un forzado):
 * `{ ok: true }` (es nuestra, o no hay lease: sin backend / sin red) o
 * `{ ok: false, charging: true }` (otro dispositivo la está cobrando).
 *
 * 2026-10-07 · Pide el lease AHORA si no lo tenemos ni hay uno en curso.
 * Antes solo esperaba a que el bridge lo pidiera y, si en 1,5 s nadie lo
 * hacía (re-click en la mesa ya seleccionada, restaurar tras recargar,
 * móvil en el plano), resolvía «ok» y la cuenta se pintaba aunque otro la
 * estuviera cobrando. `maxWaitMs` solo cubre un servidor colgado.
 */
export function whenLeaseSettled(mesaId, { maxWaitMs = 8000 } = {}) {
  if (!mesaId || unsupported) return Promise.resolve({ ok: true, noLease: true });
  // Fuera del TPV no se toma ninguna mesa (nadie pinta su cuenta ahí).
  if (!tpvRouteActive) return Promise.resolve({ ok: true, noLease: true, outsideTpv: true });
  const key = String(mesaId);
  if (myLeases()[mesaId]) return Promise.resolve({ ok: true });
  if (String(getState()?.mesaLeases?.chargingPrompt?.mesaId) === key) {
    return Promise.resolve({ ok: false, charging: true });
  }
  const pending = acquiring.has(key) ? waitAcquire(key) : setTarget(mesaId);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: true, timeout: true }), maxWaitMs);
    Promise.resolve(pending)
      .then((r) => {
        clearTimeout(timer);
        if (r?.charging) resolve({ ok: false, charging: true });
        else resolve({ ok: true, ...(r?.ok ? {} : { noLease: true }) });
      })
      .catch(() => {
        clearTimeout(timer);
        resolve({ ok: true, noLease: true });
      });
  });
}

function ensureHeartbeat() {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(heartbeat, HEARTBEAT_MS);
}

function stopHeartbeatIfIdle() {
  if (heartbeatTimer && heartbeatLeaseIds(myLeases()).length === 0) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

const isUnsupported = (err) => {
  const s = err?.status ?? err?.response?.status;
  return s === 404 || s === 405 || s === 501;
};

async function acquire(mesaId, mode = LEASE_MODE_EDIT, force = false, opts = {}) {
  if (!mesaId || unsupported) return { ok: false };
  acquiring.add(String(mesaId));
  const result = await acquireOnce(mesaId, mode, force, opts);
  settleLease(mesaId, result);
  return result;
}

// 2026-10-26 · El lease exige la cabecera SessionIdentifier (400
// DEVICE_REQUIRED sin ella). Sin identificador de dispositivo (ni recuperable)
// no se lanza la petición: el TPV sigue sin lease, como sin backend.
async function postLease(mesaId, mode, force) {
  const sid = await ensureDeviceSessionIdentifier();
  if (!sid) return { noDevice: true };
  try {
    return { res: await axios.post(`mesas/${mesaId}/lease`, { mode, force: !!force }) };
  } catch (err) {
    // La cabecera viajó vacía aunque había identificador (se perdió en
    // memoria): se re-aplica y se repite UNA vez. Nunca en bucle.
    if (!isDeviceRequiredError(err)) throw err;
    const again = await ensureDeviceSessionIdentifier();
    if (!again) return { noDevice: true };
    try {
      return { res: await axios.post(`mesas/${mesaId}/lease`, { mode, force: !!force }) };
    } catch (err2) {
      if (isDeviceRequiredError(err2)) return { noDevice: true };
      throw err2;
    }
  }
}

async function acquireOnce(mesaId, mode, force, { silent = false } = {}) {
  try {
    const { res, noDevice } = await postLease(mesaId, mode, force);
    if (noDevice) return { ok: false, noDevice: true };
    const d = res?.data || {};
    if (!d.leaseId) return { ok: false };
    // Si mientras tanto el objetivo cambió a otra mesa, soltamos esta.
    if (target?.mesaId !== mesaId) {
      releaseNow(mesaId, d.leaseId);
      return { ok: false, stale: true };
    }
    dispatch(myLeaseSet({ mesaId, leaseId: d.leaseId, mode: d.mode || mode, holder: d.holder, expiresAt: d.expiresAt }));
    setCurrentLease({ mesaId, leaseId: d.leaseId, mode: d.mode || mode });
    ensureHeartbeat();
    return { ok: true, lease: d };
  } catch (err) {
    const info = leaseErrorInfo(err);
    if (info?.code === "LEASE_CHARGING") {
      // En la re-adquisición silenciosa no preguntamos «¿Forzar?»: si otro
      // está cobrando, la mesa ya no es nuestra (takeover).
      if (!silent) dispatch(setChargingPrompt({ mesaId, holder: info.holder, mode }));
      return { ok: false, charging: true, holder: info.holder };
    }
    if (isUnsupported(err)) unsupported = true;
    return { ok: false, error: err };
  }
}

// Mesa "dentro" según el diario (el swap TPV grande ⇄ carta suelta con
// gracia y la vuelve a pedir: eso no es salir y volver a entrar).
let journalMesa = null;

function releaseNow(mesaId, leaseIdOverride) {
  if (!leaseIdOverride && journalMesa && String(journalMesa) === String(mesaId)) {
    journal.track("mesa.leave", undefined, { mesaId });
    journalMesa = null;
  }
  const leaseId = leaseIdOverride || myLeases()[mesaId]?.leaseId;
  if (!leaseIdOverride) {
    dispatch(myLeaseRemoved(mesaId));
    clearCurrentLease(mesaId);
  }
  stopHeartbeatIfIdle();
  if (!leaseId || unsupported) return;
  axios.delete(`mesas/lease/${leaseId}`).catch(() => {});
}

function cancelPendingRelease() {
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
  releasePendingMesa = null;
}

/**
 * Entramos en `mesaId` con un soltado pendiente (swap de layout): si la
 * pendiente es OTRA mesa se suelta ya. Antes se cancelaba sin más y, en
 * móvil (tocar otra mesa = cambiar de layout en el mismo tick), la mesa
 * anterior quedaba con lease y presencia para siempre (el heartbeat la
 * renovaba).
 */
function settlePendingRelease(mesaId) {
  const pending = releasePendingMesa;
  cancelPendingRelease();
  if (pending && String(pending) !== String(mesaId)) releaseNow(pending);
}

/**
 * Mesa en la que está este dispositivo (null = ninguna).
 * `graceful` → al soltar, espera RELEASE_GRACE_MS por si otro layout la
 * vuelve a pedir (swap TPV grande ⇄ carta).
 */
export function setTarget(mesaId, { mode = LEASE_MODE_EDIT, graceful = false } = {}) {
  if (mesaId && !tpvRouteActive) return Promise.resolve({ ok: false, outsideTpv: true });
  if (mesaId) ensureReacquireWatcher();
  const prev = target?.mesaId || null;
  if (mesaId) {
    settlePendingRelease(mesaId);
    if (prev && prev !== mesaId) releaseNow(prev);
    if (!journalMesa || String(journalMesa) !== String(mesaId)) {
      if (journalMesa) journal.track("mesa.leave", undefined, { mesaId: journalMesa });
      journal.track("mesa.enter", undefined, { mesaId });
      journalMesa = mesaId;
    }
    const held = myLeases()[mesaId];
    target = { mesaId, mode: held?.mode || mode };
    if (held) {
      setCurrentLease({ mesaId, leaseId: held.leaseId, mode: held.mode });
      return Promise.resolve({ ok: true });
    }
    // Ya hay un acquire en curso de esta mesa (lo pidió la entrada en la
    // mesa o el otro layout): no repetir el POST, esperar su resultado.
    if (acquiring.has(String(mesaId))) return waitAcquire(String(mesaId));
    inflight = acquire(mesaId, target.mode);
    return inflight;
  }
  // Soltar
  target = null;
  if (!prev) return Promise.resolve({ ok: true });
  if (graceful) {
    settlePendingRelease(prev);
    releasePendingMesa = prev;
    releaseTimer = setTimeout(() => {
      releaseTimer = null;
      releasePendingMesa = null;
      if (!target || target.mesaId !== prev) releaseNow(prev);
    }, RELEASE_GRACE_MS);
  } else {
    releaseNow(prev);
  }
  return Promise.resolve({ ok: true });
}

/** Sube a "charge" (abre cobro) o baja a "edit" (cierra sin cobrar). */
export async function setMode(mesaId, mode) {
  if (!mesaId || unsupported) return { ok: false };
  if (inflight) {
    try { await inflight; } catch { /* ignore */ }
  }
  const held = myLeases()[mesaId];
  // Solo sobre la mesa en la que estoy: tras un cobro la mesa ya se ha
  // soltado y el cierre del diálogo NO debe volver a tomarla.
  if (!held && target?.mesaId !== mesaId) return { ok: false };
  if (held?.mode === mode) return { ok: true };
  if (target?.mesaId === mesaId) target = { mesaId, mode };
  // POST del mismo endpoint: renueva/sube/baja con el mismo leaseId.
  return acquire(mesaId, mode, false);
}

/** El camarero confirma "Forzar" en el diálogo de mesa cobrándose. */
export async function forceTakeover() {
  const prompt = getState()?.mesaLeases?.chargingPrompt;
  dispatch(setChargingPrompt(null));
  if (!prompt?.mesaId) return { ok: false };
  target = { mesaId: prompt.mesaId, mode: prompt.mode || LEASE_MODE_EDIT };
  journal.track("lease.forced", { holder: prompt.holder?.employeeName ?? null, device: prompt.holder?.deviceLabel ?? null }, { mesaId: prompt.mesaId });
  return acquire(prompt.mesaId, target.mode, true);
}

/** Cancela el diálogo: no entramos en la mesa. */
export function cancelTakeover() {
  const prompt = getState()?.mesaLeases?.chargingPrompt;
  dispatch(setChargingPrompt(null));
  if (prompt?.mesaId && target?.mesaId === prompt.mesaId) target = null;
  return prompt?.mesaId || null;
}

/** Me han quitado la mesa (hub revoked / heartbeat lost / 409 LEASE_HELD). */
export function markRevoked(mesaId, holder) {
  if (!mesaId) return;
  const hadIt = !!myLeases()[mesaId] || target?.mesaId === mesaId;
  clearCurrentLease(mesaId);
  if (target?.mesaId === mesaId) target = null;
  if (hadIt) dispatch(leaseRevoked({ mesaId, holder }));
  stopHeartbeatIfIdle();
}

/**
 * El reducer ya detectó el takeover (evento del hub): solo sincronizamos
 * el estado interno del manager sin volver a despachar ni soltar (el
 * lease ya no es nuestro; un DELETE sería ruido).
 */
export function acknowledgeRevoked(mesaId) {
  clearCurrentLease(mesaId);
  if (target?.mesaId === mesaId) target = null;
  stopHeartbeatIfIdle();
}

async function heartbeat() {
  const ids = heartbeatLeaseIds(myLeases());
  if (!ids.length || unsupported) {
    stopHeartbeatIfIdle();
    return;
  }
  try {
    const res = await axios.post("mesas/lease/heartbeat", { leaseIds: ids });
    const lost = lostMesaIds(myLeases(), res?.data);
    // Perder el lease no implica que otro lo tenga (p. ej. el móvil estuvo
    // congelado > TTL): se decide con la presencia fresca.
    for (const mesaId of lost) await handleLostLease(mesaId);
  } catch (err) {
    if (isUnsupported(err)) unsupported = true;
    // Red caída: el servidor expirará el lease a los 30 s; al volver la
    // red el heartbeat devolverá `lost` y lo trataremos como revocado.
  }
}

let lostInflight = null;

/**
 * Lease perdido sin takeover explícito (heartbeat `lost`, `expired`, vuelta
 * de segundo plano). Nunca pierde líneas sin enviar: el takeover usa el
 * flujo existente (borrador + salir al plano).
 */
export async function handleLostLease(mesaId) {
  if (!mesaId || unsupported) return "ignored";
  if (lostInflight) return lostInflight;
  lostInflight = (async () => {
    const inside = target?.mesaId === mesaId;
    if (!inside) {
      dispatch(myLeaseRemoved(mesaId));
      clearCurrentLease(mesaId);
      stopHeartbeatIfIdle();
      return "release";
    }
    await refreshPresence();
    const holder = getState()?.mesaLeases?.byMesaId?.[mesaId]?.holder || null;
    const decision = decideLostLease({ inside, presenceHolder: holder, myDeviceId: getDeviceId() });
    if (decision === "takeover") {
      markRevoked(mesaId, holder);
      return decision;
    }
    const mode = target?.mode || LEASE_MODE_EDIT;
    const res = await acquire(mesaId, mode, false, { silent: true });
    const held = !res.ok && leaseErrorInfo(res.error)?.code === "LEASE_HELD";
    if (!res.ok && (res.charging || held)) {
      // Otro entró entre la presencia y el acquire (o está cobrando).
      markRevoked(mesaId, res.holder || leaseErrorInfo(res.error)?.holder || null);
      return "takeover";
    }
    // Sin red: se reintentará en el siguiente heartbeat / vuelta a primer plano.
    return res.ok ? "reacquired" : "retry";
  })().finally(() => {
    lostInflight = null;
  });
  return lostInflight;
}

/**
 * Vuelta a primer plano / reconexión: comprueba ya (sin esperar al tick de
 * 10 s) que el lease de la mesa en la que estoy sigue siendo mío.
 */
export async function resumeLease() {
  if (unsupported) return;
  ensureReacquireWatcher();
  const mesaId = target?.mesaId;
  if (!mesaId) return;
  if (myLeases()[mesaId]?.leaseId) {
    await heartbeat();
  } else {
    await handleLostLease(mesaId);
  }
}

// `expired` de MI lease recibido por el hub (o en el reenvío al volver):
// el reducer lo marca y aquí se resuelve sin aviso si sigue libre.
// Suscripción perezosa (al entrar en la primera mesa): suscribirse al
// cargar el módulo podría correr antes de que exista el store (ciclo de imports).
let lastReacquireTs = null;
let reacquireWatcher = null;
function ensureReacquireWatcher() {
  if (reacquireWatcher || !store?.subscribe) return;
  reacquireWatcher = store.subscribe(() => {
    const req = getState()?.mesaLeases?.reacquireRequest;
    if (!req || req.ts === lastReacquireTs) return;
    lastReacquireTs = req.ts;
    // Fuera del dispatch actual (no anidar despachos en el subscriber).
    setTimeout(() => {
      dispatch(reacquireHandled());
      clearCurrentLease(req.mesaId);
      handleLostLease(req.mesaId);
    }, 0);
  });
}

/** Snapshot de presencia (montaje del TPV y reconexión del hub). */
export async function refreshPresence() {
  if (unsupported || !tpvRouteActive) return;
  try {
    const res = await axios.get("mesas/leases");
    dispatch(leasesSnapshotReceived(res?.data || []));
  } catch (err) {
    if (isUnsupported(err)) unsupported = true;
  }
}

/**
 * Último cobro del restaurante (al cargar la app y tras reconectar el hub):
 * así un dispositivo que estaba apagado ve el último cobro hecho en otro.
 */
export async function refreshLastPayment() {
  try {
    const res = await axios.get("pedidos/pagos/last", { params: { limit: 1 } });
    const list = Array.isArray(res?.data) ? res.data : [];
    if (list[0]) dispatch(receiveLastPayment(lastPaymentFromEvent(list[0], getDeviceId())));
  } catch {
    /* endpoint no disponible o sin red: nos quedamos con el persistido */
  }
}

/**
 * Suelta todo al cerrar la pestaña/app. `fetch` con keepalive porque
 * axios no sobrevive a `pagehide`.
 */
export function releaseAllOnPageHide() {
  const leases = Object.values(myLeases()).filter((l) => l?.leaseId);
  if (!leases.length || unsupported) return;
  const base = (axios.defaults?.baseURL || "").replace(/\/$/, "");
  const common = axios.defaults?.headers?.common || {};
  const headers = {
    Authorization: common.Authorization || "",
    SessionIdentifier: common.SessionIdentifier || getDeviceId(),
  };
  try {
    const emp = window.localStorage.getItem("EmployeeSessionToken");
    if (emp) headers["X-Employee-Session"] = emp;
  } catch { /* ignore */ }
  leases.forEach((l) => {
    try {
      fetch(`${base}/mesas/lease/${l.leaseId}`, { method: "DELETE", headers, keepalive: true });
    } catch { /* ignore */ }
  });
}

export const getTargetMesaId = () => target?.mesaId || null;
export const getMyDeviceId = () => getDeviceId();
export const currentLease = () => getCurrentLease();

// 409 LEASE_HELD en cualquier mutación → la mesa es de otro.
setLeaseHeldHandler((info) => {
  const mesaId = getCurrentLease()?.mesaId || target?.mesaId;
  if (mesaId) markRevoked(mesaId, info?.holder || null);
});

// Solo para tests.
export function __resetForTests() {
  tpvRouteActive = true;
  target = null;
  acquiring.clear();
  settleWaiters.clear();
  journalMesa = null;
  inflight = null;
  unsupported = false;
  cancelPendingRelease();
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}
